import {Injectable, EventEmitter, OnDestroy} from '@angular/core';
import {SpeechRecognitionService} from '@llm/service/speech_recognition.service';
import {Subscription} from 'rxjs';
import {signal, inject, effect} from '@angular/core';
import {llmStoreService} from '@llm/service/llm_store.service';
import {Tools_aiConsoleService} from "@llm/service/tools_ai-console.service";

/**
 * Serviço responsável por processar as transcrições de voz recebidas do
 * SpeechRecognitionService e identificar comandos predefinidos.
 * Emite eventos quando um comando é detetado.
 */
@Injectable({providedIn: 'root'})
export class VoiceCommandService implements OnDestroy {
  //Injectables
  private _speechRecognitionService = inject(SpeechRecognitionService);
  private store = inject(llmStoreService);
  private _console = inject(Tools_aiConsoleService);

  /**
   * EventEmitter que emite o nome do comando detetado.
   * Exemplo: 'OpenChatNitro', 'FechouGrab', 'FecharChat', 'SilentVoiceUser'.
   */
  public commandDetected = new EventEmitter<string>();

  private lastCommand: string = '';
  private lastCommandTime: number = 0;
  private bufferCommand: string[] = [];
  private transcriptSubscription: string | null = null;
  private silentUserSubscription: Subscription | undefined;

  // Sinal para controlar qual comando ativou o modo "Nitro" (se aplicável)
  public commandActivedByNitro = signal<string>('');

  constructor() {
    this.setupListeners();
  }

  /**
   * Configura os listeners para as transcrições de voz e eventos de silêncio
   * do _speechRecognitionService.
   */
  private setupListeners(): void {
    this.transcriptSubscription = this._speechRecognitionService.transcriptionSession()
    if (this.transcriptSubscription) {
      this.processCommand(this.transcriptSubscription);
      this._console.log('Transcript: ', this.transcriptSubscription);
      }
  }

  /**
   * Processa a transcrição de voz para identificar comandos.
   * Aplica lógica de normalização e evita comandos repetidos rapidamente.
   * @param transcript A transcrição de voz em bruto.
   */
  private processCommand(transcript: string): void {
    const normalized = transcript.toLowerCase().trim().replace(/[^a-z0-9\s]/g, '');
    this._console.log('VoiceCommandService: Comando normalizado:', normalized);
    const now = Date.now();

    // Lógica para evitar comandos duplicados em curto espaço de tempo
    if (this.lastCommand === normalized && (now - this.lastCommandTime) < 3000) {
      this._console.log('VoiceCommandService: Comando duplicado ignorado:', normalized);
      return;
    }

    this.lastCommand = normalized;
    this.lastCommandTime = now;

    // Lógica de deteção de comandos específicos(Verifica se o comando já está no buffer para evitar múltiplos disparos se a transcrição for contínua)
    if (normalized.includes('oi gloria') && this.store.commandActivedByNitro() !== 'oi gloria' && !this.store.bufferCommandActivedByNitro().includes('oi gloria')) {
      this.bufferCommand.push('oi gloria');
      this.store.commandActivedByNitro.set('oi gloria');
      this.commandDetected.emit('OpenChatNitro');
      this._console.log('VoiceCommandService: Comando "OpenChatNitro" detetado.');
    } else if (normalized.includes('fechou') && this.store.commandActivedByNitro() !== 'fechou' && !this.store.bufferCommandActivedByNitro().includes('fechou')) {
      this.bufferCommand.push('fechou');
      this.store.commandActivedByNitro.set('fechou');
      this.commandDetected.emit('FechouGrab');
      this._console.log('VoiceCommandService: Comando "FechouGrab" detetado.');
    } else if (normalized.includes('valeu') && this.store.commandActivedByNitro() !== 'valeu' && !this.store.bufferCommandActivedByNitro().includes('valeu')) {
      this.bufferCommand.push('valeu');
      this.store.commandActivedByNitro.set('valeu');
      this.commandDetected.emit('FecharChat');
      this._console.log('VoiceCommandService: Comando "FecharChat" detetado.');
    }
  }

  /**
   * Remove um comando do buffer de comandos processados.
   * Isso é útil se um comando precisa ser "reutilizável" após ser acionado.
   * @param command O comando a ser removido do buffer.
   */
  public removeCommandFromBuffer(command: string): void {
    this.store.setBufferCommandActivedByNitroRemoveCommand(command);
  }

  ngOnDestroy(): void {
    this.store.bufferCommandActivedByNitro.update(() => []);
    this._console.log(`[llmStoreService] Buffer de comandos ativos limpo em ngOnDestroy.`);
  }
}
